import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { LinkHighlightArea } from "scmp-app/components/content/content-item-render/highlight/styles";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { DataMap } from "scmp-app/components/plus/plus-article-type-title/consts";
import type { helpersGetArticleTypeArticle$key } from "scmp-app/queries/__generated__/helpersGetArticleTypeArticle.graphql";
import type { plusCftContentItemContent$key } from "scmp-app/queries/__generated__/plusCftContentItemContent.graphql";

import {
  Container,
  ContentLink,
  CoverImage,
  CoverLink,
  Footer,
  Header,
  Headline,
  StyledPlusArticleTypeTitle,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  onClick?: () => void;
  reference: plusCftContentItemContent$key;
};

export const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
}) => {
  const entity = useFragment(
    graphql`
      fragment plusCftContentItemContent on Content {
        ...entityLink
        ...helpersCheckIsPlusArticle
        ...helpersGetArticleTypeArticle
        ...hooksContentItemProvidersContent
          @arguments(
            withActionBar: true
            withAuthorLink: true
            withCoverImage: true
            withCoverImageSize540x360: true
            withHeadline: true
          )
        ...plusArticleTypeTitleContent
      }
    `,
    reference,
  );

  const articleTypeId = getArticleType(entity as unknown as helpersGetArticleTypeArticle$key);
  const hasArticleTypeTitle = Boolean(articleTypeId && DataMap[articleTypeId]?.title);

  const providers = useContentItemProviders(entity);

  return (
    <Container className={className} ref={ref}>
      <CoverImage>
        <CoverLink onClick={onClick}>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size540x360",
              mobileUp: "size540x360",
              tabletUp: "size540x360",
            },
          })}
        </CoverLink>
      </CoverImage>
      <Header>
        {hasArticleTypeTitle && <StyledPlusArticleTypeTitle reference={entity} />}
        <LinkHighlightArea>
          <ContentLink onClick={onClick}>
            <Headline>
              {providers.headline({
                preferSocialHeadline: true,
              })}
            </Headline>
          </ContentLink>
        </LinkHighlightArea>
        <Footer>
          {providers.actionBar({
            displayDateVariant: "publishedDate",
            isBookmarkVisible: false,
            isCommentsVisible: false,
            isIconVisible: false,
          })}
        </Footer>
      </Header>
    </Container>
  );
};

Component.displayName = "ContentItemPlusCft";
export const ContentItemPlusCft = withHighlightedHeadline(Component);
